import React from 'react';
import { useState, useRef, useEffect } from 'react';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import SendIcon from '@mui/icons-material/Send';
import { useSelector } from 'react-redux';
import MLTextField from 'components/common/TextField/TextField';

const TimelineRow = ({ accent, initial, title, timestamp, body, isLast }) => (
  <div className={`relative pl-9 ${isLast ? '' : 'pb-5'}`}>
    {!isLast && (
      <div className="absolute left-[13px] top-6 bottom-0 w-px bg-[var(--ts-border)]" />
    )}
    <div
      className="absolute left-0 top-0 flex h-[26px] w-[26px] items-center justify-center rounded-full text-xs font-semibold"
      style={{ backgroundColor: `${accent}1f`, color: accent }}
    >
      {initial}
    </div>
    <div className="flex items-baseline justify-between gap-2">
      <Typography variant="body2" className="!font-semibold">
        {title}
      </Typography>
      <Typography variant="caption" className="whitespace-nowrap text-[var(--Grey-70)]">
        {timestamp}
      </Typography>
    </div>
    {body && (
      <Typography variant="body2" className="mt-0.5 text-[var(--ts-text-dark)]">
        {body}
      </Typography>
    )}
  </div>
);

const ActivityComments = ({
  comments,
  colourScheme = 'display',
  classList = '',
  addCommentFunc = () => {},
  updateCommentFunc = () => {},
  commentText = '',
}) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const currUser = useSelector((state) => state.global.userId);

  const [newComment, setNewComment] = useState('');

  const bottomRef = useRef(null);

  useEffect(() => {
    // Scrolls to the dummy div every time comments update
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  return (
    <div
      className={`activity-comments flex w-full flex-col rounded-2xl border border-[var(--ts-border)] bg-[var(--ts-surface)] p-4 ${classList}`}
    >
      <Typography variant="h4" className="!font-bold mb-3">
        Comments
      </Typography>
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-[var(--ts-primary)] scrollbar-track-transparent">
        {comments.length === 0 && (
          <Typography variant="body1" className="text-center text-[var(--Grey-70)]">
            No comments yet.
          </Typography>
        )}
        {comments.length > 0 &&
          [...comments]
            .sort((a, b) => new Date(a.CommentDate) - new Date(b.CommentDate))
            .map((comment, index, arr) => (
              <TimelineRow
                key={index}
                accent={
                  comment.UserID === currUser ? 'var(--ts-primary)' : 'var(--ts-accent)'
                }
                initial={comment.FullName?.[0]?.toUpperCase() ?? '?'}
                title={comment.FullName}
                timestamp={dayjs.utc(comment.CommentDate).local().format('D MMM h:mm A')}
                body={comment.CommentText}
                isLast={index === arr.length - 1}
              />
            ))}
        <div ref={bottomRef} />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <MLTextField
          placeholder="Enter your comment..."
          classList="!rounded-full"
          setTextFieldvalue={setNewComment}
          defaultVal={newComment}
        />
        <div
          className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[var(--ts-primary)] text-white"
          onClick={() => {
            if (newComment.trim() !== '') {
              addCommentFunc({ commentText: newComment });
              setNewComment('');
            }
          }}
        >
          <SendIcon fontSize="small" />
        </div>
      </div>
    </div>
  );
};

export default ActivityComments;
